import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    differenceInDays,
    addDays,
    subDays,
    startOfDay,
    addMonths,
    endOfMonth,
    startOfMonth,
} from 'date-fns';
import { isAdminApp } from '~/public/lib/PageHelpers.js';
import { clientFormatDateLong } from '~/public/lib/ClientDateTime.js';
import { useAvailabilityQuery } from '~/lib/SharedQueries.js';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { LicenseInfo } from '@mui/x-license';
import { isMobile } from '~/lib/Utils.js';
import { formatInternalDate } from '~/lib/DateTime.js';
import { ThemeContext } from '~/lib/ThemeContext.js';
import { alpha } from '@mui/material/styles';
import Loading from '~/components/Loading';

LicenseInfo.setLicenseKey(
    'f924b08074b1e9a6053af7b5a1be8285Tz0xMTY4NjMsRT0xNzg1NDU1OTk5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI=',
);


const VariableMultidayPicker = ({
    value,
    onChange,
    eventType,
    clientTz,
    quantity = 1,
    bookableResourceId,
    extraTime,
}) => {
    const containerRef = useRef();
    const [isInModal, setIsInModal] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [rangePosition, setRangePosition] = useState('');

    // Get theme color from context
    const { color: themeColor } = useContext(ThemeContext);
    const color = themeColor || '#2C87F3'; // fallback to default
    const lightColor = alpha(color, 0.35); // translucent highlight for range

    const minDays =
        eventType?.duration > 0 ? parseInt(eventType.duration / 1440) : 1;
    const maxDays = parseInt(eventType?.settings?.max_days) || 30;

    // Track the current month being viewed in the date picker
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Calculate date range for availability query based on visible months
    const [start, end] = useMemo(() => {
        // StaticDateRangePicker with calendars={2} shows 2 months
        const startDate = startOfMonth(currentMonth);
        const endDate = endOfMonth(addMonths(currentMonth, 1)); // Show 2 months
        return [startDate, endDate];
    }, [currentMonth]);

    // Query availability data
    const opts = {};
    if (extraTime) {
        opts.extraTime = extraTime;
    }

    const { data, isLoading } = useAvailabilityQuery(
        eventType?.id,
        bookableResourceId,
        start,
        end,
        clientTz,
        opts,
    );

    // Process availability data
    const times = (data || []).filter(time => time.quantity >= quantity);
    const timesByDay = {};
    for (const time of times) {
        timesByDay[time.clientDate] = [
            ...(timesByDay[time.clientDate] || []),
            time,
        ];
    }

    // Calculate max date based on start date selection
    const calculateMaxDate = startDate => {
        if (!startDate) return null;

        // Start checking from the day after the start date
        let checkDate = addDays(startDate, 1);
        const absoluteMaxDate = addDays(startDate, maxDays - 1);

        // Find the first disabled date (no availability) or reach maxDays limit
        for (let i = 1; i < maxDays; i++) {
            const dateKey = formatInternalDate(checkDate, clientTz);
            const timesForDate = timesByDay[dateKey] || [];

            if (timesForDate.length === 0) {
                // Found first unavailable date, max date is the day before
                return addDays(checkDate, -1);
            }

            checkDate = addDays(checkDate, 1);
        }

        // No unavailable dates found within maxDays, use maxDays limit
        return absoluteMaxDate;
    };

    // Calculate min date based on end date selection
    const calculateMinDate = endDate => {
        if (!endDate) return null;

        // Start checking from the day before the end date
        let checkDate = subDays(endDate, 1);
        const absoluteMinDate = subDays(endDate, maxDays - 1);

        // Find the first disabled date (no availability) going backward or reach maxDays limit
        for (let i = 1; i < maxDays; i++) {
            const dateKey = formatInternalDate(checkDate, clientTz);
            const timesForDate = timesByDay[dateKey] || [];

            if (timesForDate.length === 0) {
                // Found first unavailable date, min date is the day after
                return addDays(checkDate, 1);
            }

            checkDate = subDays(checkDate, 1);
        }

        // No unavailable dates found within maxDays, use maxDays limit
        return absoluteMinDate;
    };

    // Handle date range change
    const handleDateRangeChange = newValue => {
        const [newStart, newEnd] = newValue;
        setDateRange(newValue);

        if (newStart && newEnd) {
            const daysDifference = differenceInDays(newEnd, newStart);

            // Validate against min/max days
            if (daysDifference < minDays - 1) {
                // Adjust end date to meet minimum
                const adjustedEnd = addDays(newStart, minDays - 1);
                setDateRange([newStart, adjustedEnd]);
                updateValue(newStart, adjustedEnd);
            } else if (daysDifference > maxDays - 1) {
                // Adjust end date to meet maximum
                const adjustedEnd = addDays(newStart, maxDays - 1);
                setDateRange([newStart, adjustedEnd]);
                updateValue(newStart, adjustedEnd);
            } else {
                updateValue(newStart, newEnd);
            }
        }
    };

    const updateValue = (start, end) => {
        if (start && end) {
            const daysDifference = differenceInDays(end, start) + 1; // +1 because we include both start and end days
            const additionalDuration = (daysDifference - 1) * 24 * 60; // Additional minutes beyond the first day

            // Get the first available time for the start date
            const startDateKey = formatInternalDate(start, clientTz);
            const timesForStartDate = timesByDay[startDateKey] || [];

            let startTime;
            if (timesForStartDate.length > 0) {
                // Use the first available time
                startTime = timesForStartDate[0].time;
            } else {
                // Fallback to start of day if no availability (shouldn't happen with proper validation)
                startTime = startOfDay(start).toISOString();
            }

            onChange({
                startTime: startTime,
                additionalDuration: additionalDuration,
            });
        }
    };

    // Check if component is inside a modal panel
    useEffect(() => {
        const checkModalContainer = () => {
            if (containerRef.current) {
                const modalPanel = containerRef.current.closest('.modal-panel');
                const sidePanel = containerRef.current.closest('.side-panel');
                setIsInModal(!!modalPanel || !!sidePanel);
            }
        };

        checkModalContainer();
    }, []);

    // Responsive Calendars (onload and resize)
    const [calendars, setCalendars] = useState(2); // Default to desktop

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const checkShouldUseOneCalendar = () => {
        const isNarrow = mediaQuery.matches || isInModal;
            setCalendars(isNarrow ? 1 : 2);
        };

        // Run once on mount
        checkShouldUseOneCalendar();

        // Listen for screen size changes
        mediaQuery.addEventListener('change', checkShouldUseOneCalendar);

        return () => {
            mediaQuery.removeEventListener('change', checkShouldUseOneCalendar);
        };
    }, [isInModal]);

    const isNarrowView = isMobile() || isInModal;
    const isAdmin = isAdminApp();
    const isSelectingStart = rangePosition === 'start' && !!dateRange[1];
    const isSelectingEnd = rangePosition === 'end' && !!dateRange[0];

    return (
        <div ref={containerRef} className="relative">
            <div className="mt-4 panel-label">Date</div>
            {isLoading && (
                <Loading className="absolute border z-40 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 py-2 rounded-full shadow-lg" />
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StaticDateRangePicker
                    value={dateRange}
                    onChange={newValue => {
                        console.log('onChange', newValue);
                        handleDateRangeChange(newValue);
                    }}
                    onAccept={newValue => {
                        console.log('onAccept', newValue);
                    }}
                    onRangePositionChange={newRangePosition => {
                        console.log('onRangePositionChange', newRangePosition);
                        setRangePosition(newRangePosition);
                    }}
                    calendars={calendars}
                    disablePast
                    /* TODO - you can still extend the range by clicking before.
                        Maybe make it when you click and a date is already selected it resets the range?
                        */
                    maxDate={
                        isSelectingEnd ? calculateMaxDate(dateRange[0]) : null
                    }
                    minDate={
                        isSelectingStart ? calculateMinDate(dateRange[1]) : null
                    }
                    onMonthChange={newMonth => {
                        // Update the current month when user navigates
                        setCurrentMonth(newMonth);
                    }}
                    shouldDisableDate={date => {
                        // Disable dates that have no availability
                        const dateKey = formatInternalDate(date, clientTz);
                        const timesForDate = timesByDay[dateKey] || [];
                        return timesForDate.length === 0;
                    }}
                    slotProps={{
                        calendar: {
                            sx: { // Fixes jumpy repaint for taller/shorter months
                                minHeight: '360px',
                                maxHeight: '360px',
                                transition: 'all 0.3s ease',
                            },
                        },
                        actionBar: {
                            actions: [/*'clear'*/],
                            sx: {
                                '&.MuiPickersLayout-actionBar': {
                                    '@media (max-width:767px)': {
                                        justifyContent: 'flex-start',
                                    },
                                    '@media (min-width:768px)': {
                                        justifyContent: 'flex-end',
                                    },
                                },
                            },
                        },
                        toolbar: {
                            hidden: true,
                        },
                    }}
                    className={`panel ${isInModal ? 'in-modal' : ''} ${isLoading ? 'blur-sm' : ''}`}
                    sx={{
                        '& .Mui-selected.MuiDateRangePickerDay-day, & .Mui-selected.MuiDateRangePickerDay-day:hover, & .Mui-selected.MuiDateRangePickerDay-day:focus': {
                            backgroundColor: color,
                            color: 'white',
                        },
                        '& .MuiDateRangePickerDay-rangeIntervalDayHighlight': {
                            backgroundColor: lightColor,
                        },
                        '& .MuiDateRangePickerDay-rangeIntervalDayHighlight.MuiDateRangePickerDay-hiddenDayFiller': {
                            backgroundColor: '#FFFFFF',
                        },
                    }}
                />
            </LocalizationProvider>

            {/*{startDate && endDate && (*/}
            {/*    <div*/}
            {/*        className={`mt-4 p-4 rounded-lg ${isAdmin ? 'bg-gray-100' : 'bg-primary-50'}`}*/}
            {/*    >*/}
            {/*        <div className="font-semibold mb-2">Selected Dates:</div>*/}
            {/*        <div className="text-sm">*/}
            {/*            <div>*/}
            {/*                Start: {clientFormatDateLong(startDate, clientTz)}*/}
            {/*            </div>*/}
            {/*            <div>*/}
            {/*                End: {clientFormatDateLong(endDate, clientTz)}*/}
            {/*            </div>*/}
            {/*            <div className="mt-2 font-medium">*/}
            {/*                Duration: {differenceInDays(endDate, startDate) + 1}{' '}*/}
            {/*                days*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*)}*/}
        </div>
    );
};

export default VariableMultidayPicker;
